import * as React from 'react';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { createStyles, makeStyles } from '@material-ui/core';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { ListItemProps } from '@progress/kendo-react-dropdowns';
import {
  getAssetNameList,
  fetchCategoryCounts,
} from '../../store/Assets/AssetsActions';
import { CategoryCountRequest } from '../../Interfaces/Assets/CategoryCountRequest';
import { CategoryCountResponseEntry } from '../../Interfaces/Assets/CategoryCountResponseEntry';
import {
  ASSET_NAME_DEFAULT_FETCH_SIZE_BY_BLOCK,
  ASSET_NAME_SERIAL_NUMBER_FETCH_SIZE_BY_BLOCK,
  ASSET_NAME_HDD_FREE_SPACE_FETCH_SIZE_BY_BLOCK,
} from '../../utils/constants';
import { GetString } from '../StringResourceManager';
import DynamicComboBox from '../DynamicComboBox';

const useStyles = makeStyles(() =>
  createStyles({
    assetCategoryInput: { width: '250px', minWidth: '5%' },
    assetNameInput: { width: '250px', minWidth: '5%', marginLeft: 10 },
    assetValueTextInput: {
      marginLeft: 10,
    },
  })
);

export const CustomFilterAssetInfo = (props: any) => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const assetCategories: string[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.assetCategories
  );

  const assetNames: Map<string, string[]> = useSelector(
    (state: ApplicationState) => state.assetsReducer.assetNames
  );

  const assetValues: Map<CategoryCountRequest, string[]> = useSelector(
    (state: ApplicationState) => state.assetsReducer.assetValues
  );

  const categoryCounts: CategoryCountResponseEntry[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.categoryCounts
  );

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage
  );

  const isFetchingCategoryCounts: boolean = useSelector(
    (state: ApplicationState) => state.assetsReducer.isFetchingCategoryCounts
  );

  const isFetchingAssetCategories: boolean = useSelector(
    (state: ApplicationState) => state.assetsReducer.isFetchingAssetCategories
  );

  const isFetchingAssetNames: boolean = useSelector(
    (state: ApplicationState) => state.assetsReducer.isFetchingAssetNames
  );

  const [valuesStateData, setValuesStateData] = React.useState<string[]>([]);

  const [assetValuesTotalRows, setAssetValuesTotalRows] =
    React.useState<number>(
      categoryCounts.length > 0 ? categoryCounts[0].TotalRows : 0
    );

  const LOAD_MORE = GetString('LOAD_MORE');

  React.useEffect(() => {
    var updatedValuesStateData = valuesStateData;
    var lastValue = valuesStateData.pop();

    if (lastValue == LOAD_MORE) {
      updatedValuesStateData.push(LOAD_MORE);
      setValuesStateData(updatedValuesStateData);
    }
  }, [selectedLanguage]);

  React.useEffect(() => {
    const categoryFilter = props.filter?.value?.Category;
    const nameFilter = props.filter?.value?.Name;

    if (categoryFilter !== undefined)
      getAssetNameList(dispatch, categoryFilter);

    if (nameFilter !== undefined) {
      processFetchCategoryCounts(nameFilter);
    }
  }, [assetCategories]);

  const onChangeAssetCategory = (event: any) => {
    if (!assetNames.has(event.target.value)) {
      getAssetNameList(dispatch, event.target.value);
    }
    props.onFilterChange.call(undefined, {
      nextFilter: {
        ...props.filter,
        value: {
          Category: event.target.value,
          Name: undefined,
          Value: undefined,
        },
      },
    });
  };

  const processFetchCategoryCounts = (name: string) => {
    let categoryCountRequest: CategoryCountRequest = {
      Category: props.filter?.value?.Category,
      Name: name,
      OffsetRows: 0,
      RowsCount: getRowsCountByName(name),
    };

    setValuesStateData([]);
    fetchCategoryCounts(dispatch, categoryCountRequest);
  };

  const onChangeAssetName = (event: any) => {
    processFetchCategoryCounts(event.target.value);

    props.onFilterChange.call(undefined, {
      nextFilter: {
        ...props.filter,
        value: {
          Category: props.filter.value?.Category ?? '',
          Name: event.target.value,
          Value: undefined,
        },
      },
    });
  };

  const onValueTextChange = (event: any) => {
    if (event.target.value !== LOAD_MORE)
      props.onFilterChange.call(undefined, {
        nextFilter: {
          ...props.filter,
          value: {
            Category: props.filter.value?.Category ?? '',
            Name: props.filter.value?.Name ?? '',
            Value: event.target.value,
          },
        },
      });
  };

  const processAssetValues = () => {
    var values: string[] = valuesStateData;

    assetValues.forEach(function (
      value: string[],
      key: CategoryCountRequest,
      map: any
    ) {
      values = values.concat(value);
    });

    let uniqueValues: string[] = [];
    values.forEach(element => {
      if (!uniqueValues.includes(element)) {
        uniqueValues.push(element);
      }
    });

    processLoadMoreOption(uniqueValues);
  };

  React.useEffect(() => {
    processAssetValues();
  }, [assetValues]);

  React.useEffect(() => {
    if (categoryCounts.length > 0)
      setAssetValuesTotalRows(categoryCounts[0].TotalRows);
  }, [categoryCounts]);

  const itemRender = (
    li: React.ReactElement<HTMLLIElement>,
    itemProps: ListItemProps
  ) => {
    const index = itemProps.index;
    const itemChildren = (
      <span onClick={e => onClick(e, itemProps.dataItem)}>
        {li.props.children}
      </span>
    );

    return React.cloneElement(li, li.props, itemChildren);
  };

  const onClick = (
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>,
    value: any
  ) => {
    if (value == LOAD_MORE) {
      let categoryCountRequest: CategoryCountRequest = {
        Category: props.filter?.value?.Category,
        Name: props.filter?.value?.Name,
        OffsetRows: valuesStateData.length - 1,
        RowsCount: getRowsCountByName(props.filter?.value?.Name),
      };

      fetchCategoryCounts(dispatch, categoryCountRequest);
      event.stopPropagation();
    }
  };

  const getRowsCountByName = (name: string) => {
    let count = ASSET_NAME_DEFAULT_FETCH_SIZE_BY_BLOCK;
    switch (name) {
      case 'SerialNumber':
        count = ASSET_NAME_SERIAL_NUMBER_FETCH_SIZE_BY_BLOCK;
        break;
      case 'HDDFreeSpace':
        count = ASSET_NAME_HDD_FREE_SPACE_FETCH_SIZE_BY_BLOCK;
        break;
      default:
        break;
    }

    return count;
  };

  React.useEffect(() => {
    processLoadMoreOption(valuesStateData);
  }, [assetValuesTotalRows]);

  const processLoadMoreOption = (options: string[]) => {
    if (options.length > 0) {
      options = options.filter(value => value !== LOAD_MORE);
      if (options.length < assetValuesTotalRows) options.push(LOAD_MORE);

      setValuesStateData(options);
    }
  };

  return (
    <React.Fragment>
      <DynamicComboBox
        className={classes.assetCategoryInput}
        onChange={onChangeAssetCategory}
        data={assetCategories}
        value={props.filter?.value?.Category ?? ''}
        placeholder={GetString('CATEGORY_PLACEHOLDER')}
        loading={isFetchingAssetCategories}
      />
      <DynamicComboBox
        className={classes.assetNameInput}
        onChange={onChangeAssetName}
        data={assetNames.get(props.filter?.value?.Category)}
        value={props.filter?.value?.Name ?? ''}
        placeholder={GetString('NAME_PLACEHOLDER')}
        disabled={!props.filter?.value?.Category}
        loading={isFetchingAssetNames}
      />
      <DynamicComboBox
        className={classes.assetValueTextInput}
        placeholder={GetString('VALUE_PLACEHOLDER')}
        onChange={onValueTextChange}
        data={valuesStateData}
        value={props.filter?.value?.Value ?? ''}
        disabled={!props.filter?.value?.Name}
        itemRender={itemRender}
        loading={isFetchingCategoryCounts}
      />
    </React.Fragment>
  );
};
